import Button from '@atoms/Button/Button';
import Checkbox from '@atoms/Checkbox/Checkbox';
import Drawer from '@atoms/Drawer/Drawer';
import LikeButton from '@atoms/LikeButton/LikeButton';
import Text from '@atoms/Text/Text';
import SiteEnum from '@models/enums/SiteEnum';
import { IAddToWishlistProps } from '@models/IAddToWishlist';
import IFormValues from '@models/IFormValues';
import { WishlistGroupType } from '@models/IWishlistCardSwitch';
import { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm, UseFormRegister } from 'react-hook-form';

export interface WishlistCategory {
  readonly id: number;
  readonly label: string;
}

export interface IAddToWishlistLayout extends IAddToWishlistProps {
  readonly handleLike: () => void;
  readonly isLiked: boolean;
  readonly isOpen: boolean;
  readonly setIsOpen: (val: boolean) => void;
  readonly existingWishlistNames: WishlistCategory[];
  readonly newWishlistNames?: WishlistCategory[];
  readonly onSubmit: SubmitHandler<IFormValues>;
  readonly site?: SiteEnum;
  readonly saveLabel?: string;
  readonly unsaveLabel?: string;
}

const WishListGroup = ({
  group,
  register,
}: {
  group: WishlistGroupType;
  register: UseFormRegister<IFormValues>;
}) => (
  <>
    <Text variant="heading-21-light">{group.title}</Text>
    <ul className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-4 tw-pb-10 tw-pt-4">
      {group.options.map(({ id, label }) => (
        <Checkbox
          register={register}
          key={id}
          name={label}
          value={id}
          label={label}
        />
      ))}
    </ul>
  </>
);
const AddToWishlistLayout = ({
  isCard,
  wishlistDrawerLabels,
  buttonVariant,
  handleLike,
  isLiked,
  isOpen,
  setIsOpen,
  existingWishlistNames,
  newWishlistNames,
  onSubmit,
  site,
  saveLabel,
  unsaveLabel,
}: IAddToWishlistLayout) => {
  const likeButtonRef = useRef<HTMLButtonElement>(null);
  const [isValid, setIsValid] = useState(false);
  const { register, handleSubmit, reset, getValues } = useForm<IFormValues>();
  const hasMounted = useRef(false); // Track if the component has mounted

  useEffect(() => {
    if (hasMounted.current && !isOpen) {
      reset(); // Reset the form when the drawer closes
      setIsValid(false);
    } else {
      hasMounted.current = true; // Mark that the component has mounted
    }
  }, [isOpen, reset]);
  return (
    <div>
      <LikeButton
        isCard={isCard}
        onLike={handleLike}
        isLiked={isLiked}
        variant={buttonVariant}
        ref={likeButtonRef}
        saveLabel={saveLabel}
        unsaveLabel={unsaveLabel}
      />
      {wishlistDrawerLabels && (
        <Drawer
          title={wishlistDrawerLabels.title}
          description={wishlistDrawerLabels.subTitle}
          setIsOpen={setIsOpen}
          isOpen={isOpen}
          wrapperStyles="tw-h-full tw-flex tw-flex-col"
          site={site}
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            onChange={() => {
              const formObject = getValues();

              const hasCheckedInputs =
                Object.values(formObject).filter(Boolean).length;
              setIsValid(!!hasCheckedInputs);
            }}
            className="tw-flex tw-h-full tw-flex-col"
          >
            <div className="tw-overflow-visible tw-px-4 tw-pb-[90px] tw-pt-10 md:tw-p-12">
              {!!existingWishlistNames.length && (
                <WishListGroup
                  group={{
                    title: wishlistDrawerLabels.existingListTitle,
                    options: existingWishlistNames,
                  }}
                  register={register}
                />
              )}
              <WishListGroup
                group={{
                  title: wishlistDrawerLabels.newList.title,
                  options: newWishlistNames || [],
                }}
                register={register}
              />
            </div>
            <div className="tw-fixed tw-bottom-0 tw-left-0 tw-mt-auto tw-flex tw-w-full md:tw-sticky">
              <Button
                disabled={!isValid}
                variant={site === SiteEnum.B2B ? 'yellow' : 'grey80'}
                label={wishlistDrawerLabels.submitLabel}
                type="submit"
                buttonClasses="tw-w-full tw-rounded-none"
              />
            </div>
          </form>
        </Drawer>
      )}
    </div>
  );
};

export default AddToWishlistLayout;
